import type { BadgeTone } from '@zarya/ui';
import { t } from '@app/i18n';
import type { EstadoIdentidade } from './Identities.api';

/**
 * Vocabulário de identidade — rótulos, tons e formatações de T4/T5.
 *
 * Vive fora dos arquivos de componente porque aqueles só exportam componentes
 * (`react-refresh/only-export-components`), a mesma razão de `navigation.ts` e
 * `toastContext.ts` existirem separados no shell.
 *
 * **Rótulo e ⓘ são FUNÇÕES, não tabelas** (Onda C). Uma tabela `Record<…,
 * string>` é avaliada uma vez, na carga do módulo: trocar de idioma depois disso
 * não a alcança, e a tela repintaria com o texto do idioma anterior sem nada
 * ficar vermelho. Tom continua tabela — cor não tem idioma.
 */

/**
 * Estado exibido da identidade — os 7 estados de FR-051, com os rótulos
 * literais da tabela do spec (§4.1 do inventário do protótipo, mais os dois que
 * a aderência acrescentou: `sem atribuição` e `materialização pendente`).
 *
 * A ordem é a da tabela do FR-051 (primeira condição que casar), e é também a
 * ordem do combobox de estado em T4.
 *
 * **A chave do catálogo é o valor do enum, em snake_case**, e não um camelCase
 * nosso: `EstadoIdentidade` é vocabulário do **contrato**, e mapeá-lo para outra
 * grafia criaria uma segunda tabela para desalinhar — a mesma razão que fez
 * `error.*` ser chaveado pelo `code` do envelope na B1b (decisão de 02/09).
 */
export const STATES: EstadoIdentidade[] = [
  'descoberta_sem_atribuicao',
  'descoberta_sem_contrato',
  'nova_aguardando_aprovacao',
  'alterada_reaprovacao_pendente',
  'alterada_materializacao_pendente',
  'aprovada_aguardando_deploy',
  'aprovada_em_execucao',
];

/** Rótulo do estado exibido, no locale corrente. */
export function stateLabel(state: EstadoIdentidade): string {
  return t(`identities.state.${state}`);
}

/** Explicação ⓘ de cada estado — a condição que o derivou (FR-051, FR-132). */
export function stateTip(state: EstadoIdentidade): string {
  return t(`identities.state.${state}.tip`);
}

/** Tons do protótipo (`agentState`): warn · crit · info · ok. */
export const STATE_TOM: Record<EstadoIdentidade, BadgeTone> = {
  descoberta_sem_atribuicao: 'warning',
  descoberta_sem_contrato: 'danger',
  nova_aguardando_aprovacao: 'warning',
  alterada_reaprovacao_pendente: 'warning',
  alterada_materializacao_pendente: 'danger',
  aprovada_aguardando_deploy: 'primary',
  aprovada_em_execucao: 'success',
};

/**
 * Release sem declaração de segurança (03/09) — a leitura que faltava em T4/T5.
 *
 * A `ordem-servico` do elenco tem papel e dono, e **nunca** gera aprovação: a
 * imagem não traz o rótulo `io.segura0.agente.contrato`, então a release não pede
 * capability nenhuma e o Discovery não cria pendência (AC-18). Isso está certo — o
 * que faltava era a tela dizer. Sem esta linha, quem conta seis pendências para
 * sete identidades vai procurar a sétima (foi o que aconteceu no localhost).
 *
 * Não confundir com «Descoberta · sem contrato»: aquele estado tem duas causas
 * possíveis (release sem declaração **ou** release rejeitada), e esta leitura diz
 * qual das duas é — quando a resposta é a primeira.
 */
export function releaseWithoutDeclarationLabel(): string {
  return t('identities.releaseWithoutDeclaration.label');
}

export function releaseWithoutDeclarationTip(): string {
  return t('identities.releaseWithoutDeclaration.tip');
}

/** O máximo de caracteres do papel na coluna Papel de T4, com as reticências (aim-pjv, decisão do mrolim, 10/10/2026). */
export const ROLE_MAX_CHARS = 30;

/**
 * O papel como a coluna Papel o mostra: no máximo {@link ROLE_MAX_CHARS} caracteres, com «…» no fim quando corta. Conta
 * por caractere e não por unidade UTF-16, para não partir um acento nem um emoji ao meio. O valor inteiro vai na dica
 * (`title`), e só quando o texto foi cortado: uma dica igual ao que já está à vista é ruído.
 */
export function roleCell(label: string): { text: string; full: string | null } {
  const chars = Array.from(label);
  if (chars.length <= ROLE_MAX_CHARS) return { text: label, full: null };
  return { text: `${chars.slice(0, ROLE_MAX_CHARS - 1).join('').trimEnd()}…`, full: label };
}

// O toast "{n} imagens varridas · …" mudou de casa em 29/08: mora em
// `@app/discovery` (`mensagemDaVarredura`), junto do acompanhamento que o produz.
// Aqui ele ficava longe de quem o mostra — e o vocabulário de identidade não é
// dono do vocabulário da varredura.
